<template>
  <div class="repasses-container">
    <!-- Navegação por Ícones -->
    <CardNavLocacao :pasta="pasta" class="mb-4" />

    <!-- Cabeçalho -->
    <div class="card repasses-card mb-4">
      <div class="repasses-card-header">
        <h4 class="mb-0">Repasses</h4>
      </div>
      <div class="repasses-card-body">
        <span class="badge-campo"><strong>Pasta:</strong> {{ pasta }}</span>
        <span class="mx-2 text-muted">|</span>
        <span class="badge-campo"><strong>Referência do Imóvel:</strong> {{ locacao.LOC_IMOVEL || '---' }}</span>
      </div>
    </div>

    <!-- HEADER Informações -->
    <div class="card repasses-card mb-4">
      <div class="repasses-card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">Informações do Repasse</h5>
        <span
          class="badge-status"
          :class="{
            'pago-sim': ultimoBoleto.pago == 1,
            'pago-nao': ultimoBoleto.pago != 1
          }"
        >
          {{ ultimoBoleto.pago == 1 ? 'Último boleto pago' : 'Nenhum pagamento confirmado' }}
        </span>
      </div>
      <div class="repasses-card-body">
        <div class="row mt-2">
          <div class="col-md-6">
            <h6 class="section-subtitle">Dados do Locador</h6>
            <p><strong>Nome:</strong> {{ locacao.LOC_LOCADOR || '---' }}</p>
            <p><strong>Telefone 1:</strong> {{ locacao.LOC_LOCADOR_TELEFONE1 || '---' }}</p>
            <p><strong>Telefone 2:</strong> {{ locacao.LOC_LOCADOR_TELEFONE2 || '---' }}</p>
            <p><strong>Email:</strong> {{ locacao.LOC_LOCADOR_EMAIL || '---' }}</p>
            <p><strong>Outras informações:</strong> {{ locacao.LOC_LOCADOR_OBS || '---' }}</p>
          </div>

          <div class="col-md-6">
            <h6 class="section-subtitle">Chave PIX</h6>
            <p><strong>Tipo:</strong> {{ locacao.LOC_PIX || '---' }}</p>
            <p><strong>Chave:</strong> {{ locacao.LOC_REPASSE || '---' }}</p>
          </div>
        </div>

        <hr class="separador" />

        <div class="text-center mt-3">
          <h5 class="valor-repasse-label">Valor a ser repassado ao locador:</h5>
          <h2 class="valor-repasse-valor">{{ formatarMoeda(valorRepasse) }}</h2>
          <input type="text" v-model="valorRepasse" v-money="money" class="form-control valor-repasse-input" />
          <p class="text-muted mt-2">
            <!--Total do último boleto pago - Valor de Administração - Taxa boleto - Seguro Incêndio - S/Fiança -->
          </p>
        </div>
      </div>
    </div>

    <!-- COMPOSIÇÃO DO BOLETO -->
    <div class="card repasses-card mb-4">
      <div class="repasses-card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">Composição do Boleto</h5>
        <div class="d-flex align-items-center justify-content-end flex-wrap" style="gap: 10px;">
          <span
            class="badge-status"
            :class="{
              'pago-sim': ultimoBoleto.pago == 1,
              'pago-nao': ultimoBoleto.pago != 1
            }"
          >
            {{ ultimoBoleto.pago == 1 ? 'Pago' : 'Em aberto' }}
          </span>
          <button
            type="button"
            class="btn-editar-composicao"
            :class="{ 'ativo': composicaoEditando }"
            @click="alternarEdicaoComposicao"
          >
            {{ composicaoEditando ? 'Concluir edição' : 'Editar composição' }}
          </button>
        </div>
      </div>
      <div class="repasses-card-body">
        <div class="row composicao-dados-boleto mb-3">
          <div class="col-md-4">
            <p class="mb-1"><strong>Vencimento:</strong> {{ formatarDataBR(ultimoBoleto.vencimento) || '---' }}</p>
          </div>
          <div class="col-md-4">
            <p class="mb-1"><strong>Data de Emissão:</strong> {{ formatarDataBR(ultimoBoleto.data_emissao) || '---' }}</p>
          </div>
          <div class="col-md-4">
            <p class="mb-1"><strong>Valor Total do Boleto:</strong> {{ formatarMoeda(ultimoBoleto.valor_total) }}</p>
          </div>
        </div>

        <hr class="separador" />

        <!-- Composição do Valor -->
        <h6 class="section-subtitle mb-3">Composição do Valor</h6>

        <!-- MODO LEITURA: composição originada do boleto (sem edição) -->
        <div class="composicao-tabela" v-if="!composicaoEditando">
          <div class="composicao-linha" v-for="(item, index) in compFixos" :key="'fixo-' + index">
            <span>{{ item.descricao }}</span>
            <span class="composicao-valor">{{ formatarMoeda(item.valor) }}</span>
          </div>

          <!-- Itens adicionais (outros) -->
          <template v-if="outros.length">
            <h6 class="section-subtitle mt-3 mb-2 composicao-subitem">Outros</h6>
            <div class="composicao-linha" v-for="(item, index) in outros" :key="'outro-' + index">
              <span>{{ item.descricao || 'Item' }}</span>
              <span class="composicao-valor">{{ formatarMoeda(item.valor) }}</span>
            </div>
          </template>

          <!-- Descontos -->
          <template v-if="descontos.length">
            <h6 class="section-subtitle mt-3 mb-2 composicao-subitem">Descontos</h6>
            <div class="composicao-linha composicao-desconto" v-for="(item, index) in descontos" :key="'desconto-' + index">
              <span>{{ item.descricao || 'Desconto' }}</span>
              <span class="composicao-valor">- {{ formatarMoeda(item.valor) }}</span>
            </div>
          </template>

          <div class="composicao-linha composicao-total">
            <span>Valor Total do Boleto</span>
            <span class="composicao-valor-total">{{ formatarMoeda(totalComposicao) }}</span>
          </div>
        </div>

        <!-- MODO EDIÇÃO: permite editar/adicionar/excluir itens sem alterar
             os dados do boleto salvos no banco -->
        <div class="composicao-tabela" v-else>
          <div class="composicao-linha composicao-linha-edicao" v-for="(item, index) in compFixos" :key="'fixo-edit-' + index">
            <input type="text" v-model="item.descricao" class="form-control composicao-input-desc" />
            <input type="text" v-model="item.valor" v-money="money" class="form-control composicao-input-valor" />
            <button type="button" class="btn-remover-item" title="Remover item" @click="removerItem('compFixos', index)">×</button>
          </div>
          <button type="button" class="btn-adicionar-item" @click="adicionarItem('compFixos', 'Item')">+ Adicionar item</button>

          <h6 class="section-subtitle mt-3 mb-2 composicao-subitem">Outros</h6>
          <div class="composicao-linha composicao-linha-edicao" v-for="(item, index) in outros" :key="'outro-edit-' + index">
            <input type="text" v-model="item.descricao" class="form-control composicao-input-desc" placeholder="Descrição" />
            <input type="text" v-model="item.valor" v-money="money" class="form-control composicao-input-valor" />
            <button type="button" class="btn-remover-item" title="Remover item" @click="removerItem('outros', index)">×</button>
          </div>
          <button type="button" class="btn-adicionar-item" @click="adicionarItem('outros', '')">+ Adicionar outro</button>

          <h6 class="section-subtitle mt-3 mb-2 composicao-subitem">Descontos</h6>
          <div class="composicao-linha composicao-linha-edicao" v-for="(item, index) in descontos" :key="'desconto-edit-' + index">
            <input type="text" v-model="item.descricao" class="form-control composicao-input-desc" placeholder="Descrição" />
            <input type="text" v-model="item.valor" v-money="money" class="form-control composicao-input-valor" />
            <button type="button" class="btn-remover-item" title="Remover item" @click="removerItem('descontos', index)">×</button>
          </div>
          <button type="button" class="btn-adicionar-item" @click="adicionarItem('descontos', '')">+ Adicionar desconto</button>

          <div class="composicao-linha composicao-total">
            <span>Valor Total do Boleto</span>
            <span class="composicao-valor-total">{{ formatarMoeda(totalComposicao) }}</span>
          </div>
        </div>

        <hr class="separador" />

        <!-- Deduções para o Repasse -->
        <h6 class="section-subtitle mb-3">Deduções para o Repasse</h6>
        <div class="composicao-tabela">
          <!-- MODO LEITURA -->
          <template v-if="!composicaoEditando">
            <div class="composicao-linha composicao-deducao" v-for="(item, index) in deducoes" :key="'deducao-' + index">
              <span>(-) {{ item.descricao }}</span>
              <span class="composicao-valor">- {{ formatarMoeda(item.valor) }}</span>
            </div>
          </template>

          <!-- MODO EDIÇÃO -->
          <template v-else>
            <div class="composicao-linha composicao-linha-edicao" v-for="(item, index) in deducoes" :key="'deducao-edit-' + index">
              <input type="text" v-model="item.descricao" class="form-control composicao-input-desc" placeholder="Descrição" />
              <input type="text" v-model="item.valor" v-money="money" class="form-control composicao-input-valor" />
              <button type="button" class="btn-remover-item" title="Remover item" @click="removerItem('deducoes', index)">×</button>
            </div>
            <button type="button" class="btn-adicionar-item" @click="adicionarItem('deducoes', '')">+ Adicionar </button>
          </template>

          <div class="composicao-linha composicao-repasse-final">
            <span>Valor a Repassar</span>
            <span class="composicao-valor-repasse">{{ formatarMoeda(valorRepasseCalculado) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- GERAR PIX -->
    <div class="text-center mb-4">
      <div class="mb-3">
        <label class="fw-bold me-2">Tipo de saída:</label>
        <select v-model="tipoSaida" class="form-control select-tipo-saida">
          <option value="qr">QR Code</option>
          <option value="br">BR Code</option>
        </select>
      </div>

      <button class="btn-gerar-pix" @click="gerarPix">
        Gerar Pix
      </button>
    </div>

    <!-- RESULTADO PIX -->
    <div class="text-center my-4" v-if="qrCodeUrl || brCode">
      <div class="card repasses-card d-inline-block p-4">
        <div v-if="qrCodeUrl">
          <img :src="qrCodeUrl" alt="QR Code Pix" class="img-fluid shadow mt-3" style="max-width:300px;">
        </div>

        <div v-else class="mt-3">
          <textarea class="form-control text-center fw-bold" rows="3" v-text="brCode"></textarea>
          <button class="btn-copiar mt-3" @click="copiarBrCode">Copiar</button>
        </div>

        <button class="btn-confirmar-pix mt-4" @click="confirmarRepasse">Confirmar</button>
      </div>
    </div>

    <!-- LISTAGEM DE REPASSES -->
    <div class="card repasses-card mb-5">
      <div class="repasses-card-header">
        <h5 class="mb-0">Histórico de Repasses</h5>
      </div>
      <div class="repasses-card-body">
        <div class="table-responsive">
          <table class="table table-repasses mb-0">
            <thead>
              <tr>
                <th>Data do Repasse</th>
                <th>Valor</th>
                <th>Relatório Enviado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="repasses.length === 0">
                <td colspan="4" class="text-muted text-center">Nenhum repasse registrado ainda.</td>
              </tr>
              <tr v-for="r in repasses" :key="r.id">
                <td>{{ formatarDataBR(r.data_repasse) }}</td>
                <td class="valor-cell">R$ {{ formatarMoeda(r.valor_repasse) }}</td>
                <td>
                  <span class="badge-status" :class="r.relatorio_enviado == 1 ? 'pago-sim' : 'pago-nao'">
                    {{ r.relatorio_enviado == 1 ? 'Sim' : 'Não' }}
                  </span>
                </td>
                <td>
                  <button @click="enviarEmail(r.id)" class="btn-enviar-relatorio">Enviar Relatório</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <!-- Botão de acesso rápido - Buscar Locações -->
    <router-link to="/buscaLocacoes" class="fab-buscar-locacoes" title="Buscar Locações">
      <span class="fab-icone">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
        </svg>
      </span>
      <span class="fab-texto">Buscar Locações</span>
    </router-link>

    <!-- Navegação por Ícones (rodapé) -->
    <CardNavLocacao :pasta="pasta" class="mt-4" />

    <!-- Modal de prévia do relatório de repasse (e-mail) -->
    <modal
      name="modal-preview-relatorio"
      :adaptive="true"
      classes="vue-modal-preview-relatorio"
      width="820px"
      height="auto"
    >
      <div class="modal-preview-header">
        <h5>Prévia do Relatório de Repasse</h5>
        <button
          type="button"
          class="modal-preview-close"
          title="Fechar"
          @click="$modal.hide('modal-preview-relatorio')"
        >
          ×
        </button>
      </div>

      <div class="modal-preview-corpo">
        <!-- srcdoc renderiza o HTML do e-mail exatamente como será enviado -->
        <iframe
          v-if="previewHtml"
          :srcdoc="previewHtml"
          class="modal-preview-iframe"
          title="Prévia do relatório de repasse"
        ></iframe>
      </div>

      <div class="modal-preview-footer">
        <button
          type="button"
          class="btn-cancelar-preview"
          @click="$modal.hide('modal-preview-relatorio')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn-enviar-preview"
          :disabled="enviandoRelatorio"
          @click="confirmarEnvioRelatorio"
        >
          {{ enviandoRelatorio ? 'Enviando...' : 'Enviar Relatório' }}
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import axios from "axios";
import { VMoney } from "v-money";
import CardNavLocacao from "@/components/CardNavLocacao";
export default {
  directives: { money: VMoney },
  components: { CardNavLocacao },
  data() {
    return {
       money: {
        decimal: ",",
        thousands: ".",
        prefix: "R$ ",
        precision: 2,
      },
      baseUrl: "https://www2.royaljardim.com.br/sistema/",
      pasta: "",
      locacao: {},
      ultimoBoleto: {},
      valorRepasse: "R$ 0,00",
      tipoSaida: "qr",
      qrCodeUrl: "",
      brCode: "",
      repasses: [],
      outros: [],
      descontos: [],
      composicaoEditando: false,
      compFixos: [],
      deducoes: [],
      previewHtml: "",
      previewRepasseId: null,
      enviandoRelatorio: false,
    };
  },
  watch: {
    // Recalcula o valor do repasse sempre que a composição é alterada.
    // O usuário ainda pode ajustá-lo manualmente no campo de valor.
    valorRepasseCalculado(valor) {
      this.valorRepasse = this.formatarMoeda(valor);
    },
  },
  computed: {
    // Total da composição após as edições: linhas fixas + outros − descontos
    totalComposicao() {
      let total = 0;
      this.compFixos.forEach((item) => (total += this.parseMoeda(item.valor)));
      this.outros.forEach((item) => (total += this.parseMoeda(item.valor)));
      this.descontos.forEach((item) => (total -= this.parseMoeda(item.valor)));
      return this.arredondar(total);
    },
    // Valor calculado do repasse para exibição no breakdown
    valorRepasseCalculado() {
      let total = this.totalComposicao;
      this.deducoes.forEach((item) => (total -= this.parseMoeda(item.valor)));
      return this.arredondar(total);
    },
  },
  mounted() {
    this.pasta = this.$route.params.id;
    this.carregarDados();
    this.listarRepasses();
  },
  methods: {
      // Abre a janela modal com a prévia do relatório (HTML do e-mail) para o
      // usuário conferir os itens antes de confirmar o envio.
      enviarEmail(id_repasse) {
        axios
          .post(this.baseUrl + "boletos_editar", new URLSearchParams({
            acao: "preview_relatorio",
            id:   id_repasse
          }))
          .then((res) => {
            if (res.data && res.data.html) {
              this.previewHtml = res.data.html;
              this.previewRepasseId = id_repasse;
              this.enviandoRelatorio = false;
              this.$modal.show("modal-preview-relatorio");
            } else {
              alert("Não foi possível carregar a prévia do relatório.");
            }
          })
          .catch(() => {
            alert("Falha ao carregar a prévia do relatório. Tente novamente.");
          });
      },

      // Envia o relatório de fato, a partir do botão dentro da janela modal.
      async confirmarEnvioRelatorio() {
        if (this.enviandoRelatorio || !this.previewRepasseId) return;
        this.enviandoRelatorio = true;
        try {
          const res = await axios.post(this.baseUrl + "boletos_editar", new URLSearchParams({
            acao: "enviar_relatorio",
            id:   this.previewRepasseId
          }));
          alert(res.data.mensagem);
          this.$modal.hide("modal-preview-relatorio");
          this.previewHtml = "";
          this.previewRepasseId = null;
          this.listarRepasses();
        } catch (e) {
          console.error("Erro ao enviar relatório:", e);
          alert("Erro ao enviar o relatório. Tente novamente.");
        } finally {
          this.enviandoRelatorio = false;
        }
      },

    async carregarDados() {
      try {
        const res = await axios.post(this.baseUrl + "repasses", new URLSearchParams({
          acao: "carregar",
          pasta: this.pasta,
        }));
        const dados = res.data;
        this.locacao = dados.locacao || {};
        this.ultimoBoleto = dados.boleto || {};
        // Alimenta as listas de "outros" e descontos a partir do JSON do boleto
        this.outros = this.parseJsonSafe(this.ultimoBoleto.outros);
        this.descontos = this.parseJsonSafe(this.ultimoBoleto.descontos);
        // Alimenta as linhas fixas e as deduções do repasse a partir do
        // boleto/locação. São cópias locais (o boleto no banco não muda).
        const b = this.ultimoBoleto;
        this.compFixos = [
          { descricao: 'Valor Aluguel',  valor: b.valor_aluguel },
          { descricao: 'Condomínio',     valor: b.valor_condominio },
          { descricao: 'IPTU',           valor: b.valor_iptu },
          { descricao: 'Seguro Incêndio', valor: b.valor_seguro },
          { descricao: 'S/Fiança',       valor: b.valor_fianca },
          { descricao: 'Taxa Boleto',    valor: b.valor_taxa_boleto },
        ]
          .filter((item) => this.parseMoeda(item.valor) !== 0 || item.descricao === 'Valor Aluguel')
          .map((item) => ({ descricao: item.descricao, valor: this.formatarMoeda(item.valor) }));
        this.deducoes = [
          { descricao: 'Taxa do Boleto',       valor: b.valor_taxa_boleto },
          { descricao: 'Valor de Administração', valor: this.locacao.LOC_VALOR_ADM },
          { descricao: 'Seguro Incêndio',      valor: b.valor_seguro },
          { descricao: 'Seguro Fiança',        valor: b.valor_fianca },
        ]
          .filter((item) => this.parseMoeda(item.valor) !== 0)
          .map((item) => ({ descricao: item.descricao, valor: this.formatarMoeda(item.valor) }));
      // Armazena o valor já no formato da máscara (string) para evitar
      // que o v-money escale novamente número inteiro (ex.: 8529 -> 85,29)
      this.valorRepasse = this.formatarMoeda(
        Math.round(this.valorRepasseCalculado * 100) / 100
      );
      } catch (e) {
        console.error("Erro ao carregar dados:", e);
      }
    },

   async gerarPix() {
     
  try {
    const payload = new FormData();
    payload.append("nome", this.locacao.LOC_LOCADOR);
    payload.append("cidade", "Santo André");
    payload.append("valor", this.valorRepasse.replace(/[R$\s.]/g, "").replace(",", "."));
    payload.append("saida", this.tipoSaida);
    payload.append("chave", this.locacao.LOC_REPASSE.replace(".", "").replace(".", "").replace("-", ""));
    payload.append("txid", "Repasse");

    const response = await axios.post(`${this.baseUrl}Repasses/gerar_pix`, payload);
    console.log(response);

    if (response.data.type === "qr") {
      // exibe o QR Code
      this.qrCodeUrl = `data:image/png;base64,${response.data.data}`;
      this.brCode = null;
    } else if (response.data.type === "br") {
      // exibe o BR Code de texto
      
      this.brCode = response.data.data;
      this.qrCodeUrl = null;
    } else {
      alert("Erro ao gerar Pix. Verifique os dados e tente novamente.");
    }
  } catch (error) {
    console.error("Erro ao gerar Pix:", error);
    alert("Falha ao gerar Pix. Tente novamente mais tarde.");
  }
},

    async confirmarRepasse() {
      try {
        await axios.post(this.baseUrl + "repasses", new URLSearchParams({
          acao: "salvar",
          pasta: this.pasta,
          id_boleto: this.ultimoBoleto.id,
          valor: this.valorRepasse.replace(/[R$\s.]/g, "").replace(",", "."),
          // Snapshot da composição (valores numéricos) salvo na tabela
          // `repasses` para o relatório refletir as edições feitas na tela
          composicao: JSON.stringify(this.montarSnapshotComposicao()),
        }));
        alert("Repasse confirmado com sucesso!");
        this.listarRepasses();
      } catch (e) {
        console.error("Erro ao confirmar repasse:", e);
      }
    },

    async listarRepasses() {
      const res = await axios.post(this.baseUrl + "repasses", new URLSearchParams({
        acao: "listar",
        pasta: this.pasta,
      }));
      this.repasses = res.data || [];
    },

    async excluirRepasse(id) {
      if (confirm("Deseja excluir este repasse?")) {
        await axios.post(this.baseUrl + "repasses", new URLSearchParams({
          acao: "excluir",
          id: id,
        }));
        this.listarRepasses();
      }
    },

    async enviarRelatorio(id) {
      await axios.post(this.baseUrl + "repasses", new URLSearchParams({
        acao: "enviar_relatorio",
        id: id,
      }));
      alert("Relatório enviado ao locador!");
    },

    // Alterna entre o modo leitura (composição do boleto) e o modo edição.
    // No modo edição o usuário pode editar/adicionar/excluir itens sem
    // alterar os dados do boleto salvos no banco de dados.
    alternarEdicaoComposicao() {
      this.composicaoEditando = !this.composicaoEditando;
    },

    // Adiciona um item vazio ao grupo informado (compFixos, outros,
    // descontos ou deducoes).
    adicionarItem(grupo, descricao = "") {
      this[grupo].push({ descricao, valor: "R$ 0,00" });
    },

    // Remove um item do grupo informado, apenas do estado local (o boleto
    // salvo no banco de dados não é alterado).
    removerItem(grupo, index) {
      this[grupo].splice(index, 1);
    },

    // Monta o snapshot (JSON) da composição com valores numéricos, para ser
    // salvo na tabela `repasses` quando o usuário confirmar o repasse.
    montarSnapshotComposicao() {
      const paraNumerico = (item) => ({
        descricao: item.descricao,
        valor: this.arredondar(this.parseMoeda(item.valor)),
      });
      return {
        itens: this.compFixos.map(paraNumerico),
        outros: this.outros.map(paraNumerico),
        descontos: this.descontos.map(paraNumerico),
        deducoes: this.deducoes.map(paraNumerico),
        valor_total: this.arredondar(this.totalComposicao),
      };
    },

    // Arredonda um valor para duas casas decimais, evitando erros de ponto
    // flutuante (ex.: 0.1 + 0.2 == 0.30000000000000004).
    arredondar(v) {
      return Math.round((parseFloat(v) || 0) * 100) / 100;
    },

    // Converte valores monetários (strings do MySQL "1500.00" ou formato
    // brasileiro "R$ 1.500,00") para número, antes de formatar como moeda.
    formatarMoeda(v) {
      const n = this.parseMoeda(v);
      return n.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },

    // Faz o parse de um valor monetário para número.
    // Aceita strings do MySQL ("1500.00"), formato brasileiro ("R$ 1.500,00")
    // e números diretos.
    parseMoeda(v) {
      if (v === null || v === undefined || v === "") return 0;
      if (typeof v === "number") return v;
      let valor = v.toString().trim().replace("R$", "").replace(/\s/g, "");
      // Remove pontos de milhar, mantém a vírgula decimal
      valor = valor.replace(/\.(?=\d{3}(,|$))/g, "");
      valor = valor.replace(",", ".");
      const numero = parseFloat(valor);
      return isNaN(numero) ? 0 : numero;
    },

    // Faz o parse seguro de campos JSON (outros/descontos).
    // O backend Boletos.php faz json_encode() em valor que já é JSON string,
    // causando double-encoding. Esta função desfaz ambas as camadas.
    parseJsonSafe(valor) {
      if (Array.isArray(valor)) return valor;
      if (!valor) return [];
      try {
        let parsed = JSON.parse(valor);
        // Se após o primeiro parse ainda for string, faz o segundo decode
        if (typeof parsed === "string") {
          parsed = JSON.parse(parsed);
        }
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    },
     

    formatarDataBR(data) {
      if (!data) return "";
      const [ano, mes, dia] = data.split(" ")[0].split("-");
      return `${dia}/${mes}/${ano}`;
    },

    copiarBrCode() {
  try {
    // Verifica se o navegador suporta Clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(this.brCode)
        .then(() => alert("BR Code copiado com sucesso!"))
        .catch(() => this.copiaAlternativa());
    } else {
      // Usa método alternativo se não estiver em contexto seguro
      this.copiaAlternativa();
    }
  } catch (err) {
    console.error("Erro ao copiar BR Code:", err);
    this.copiaAlternativa();
  }
},

copiaAlternativa() {
  // Cria elemento temporário
  const textarea = document.createElement("textarea");
  textarea.value = this.brCode;
  textarea.style.position = "fixed"; // evita rolagem
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  try {
    const sucesso = document.execCommand("copy");
    if (sucesso) alert("BR Code copiado com sucesso!");
    else alert("Não foi possível copiar o BR Code.");
  } catch (e) {
    alert("Seu navegador não permite copiar automaticamente.");
  }

  document.body.removeChild(textarea);
},

    visualizarRepasse(id) {
      alert("Visualização detalhada ainda não implementada. ID: " + id);
    },
  },
};
</script>

<style scoped>
/* ===== Container ===== */
.repasses-container {
  max-width: 1100px;
  margin: 0 auto;
}

/* ===== Cards ===== */
.repasses-card {
  border: 1px solid #e8e4e3;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  overflow: hidden;
}
.repasses-card-header {
  background: #4C1C17;
  color: #fff;
  padding: 12px 20px;
}
.repasses-card-header h4,
.repasses-card-header h5 {
  color: #fff;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.repasses-card-header h4 { font-size: 17px; }
.repasses-card-header h5 { font-size: 14px; }
.repasses-card-body {
  padding: 20px;
  background: #fff;
}
.badge-campo {
  font-size: 14px;
  color: #555;
}
.section-subtitle {
  color: #4C1C17;
  font-weight: 700;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.separador {
  border: 0;
  height: 1px;
  background: linear-gradient(to right, transparent, #4C1C17, transparent);
  margin: 16px 0;
}

/* ===== Badge Status ===== */
.badge-status {
  display: inline-block;
  padding: 8px 18px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.pago-sim {
  background: #e8f5e9;
  color: #28a745;
}
.pago-nao {
  background: #ffe5e5;
  color: #dc3545;
}

/* ===== Valor Repasse ===== */
.valor-repasse-label {
  color: #4C1C17;
  font-weight: 700;
}
.valor-repasse-valor {
  color: #28a745;
  font-weight: 800;
  font-size: 32px;
}
.valor-repasse-input {
  width: 220px;
  margin: 8px auto 0;
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  color: #4C1C17;
  border: 2px solid #4C1C17;
  border-radius: 8px;
}

/* ===== Composição do Boleto ===== */
.composicao-dados-boleto {
  font-size: 14px;
  color: #555;
}
.composicao-tabela {
  max-width: 520px;
  margin: 0 auto;
}
.composicao-linha {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  font-size: 14px;
  border-bottom: 1px dashed #e8e4e3;
  color: #555;
}
.composicao-linha:last-child {
  border-bottom: none;
}
.composicao-valor {
  font-weight: 700;
  color: #4C1C17;
}
.composicao-desconto .composicao-valor,
.composicao-deducao .composicao-valor {
  color: #dc3545;
}
.composicao-subitem {
  font-size: 12px;
  color: #6c757d;
}

/* ===== Edição da Composição ===== */
.btn-editar-composicao {
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255,255,255,0.7);
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 6px;
  transition: all 0.2s;
}
.btn-editar-composicao:hover,
.btn-editar-composicao.ativo {
  background: #fff;
  color: #4C1C17;
}
.composicao-linha-edicao {
  gap: 8px;
}
.composicao-input-desc {
  flex: 1;
  font-size: 13px;
  padding: 4px 8px;
}
.composicao-input-valor {
  width: 140px;
  text-align: right;
  font-size: 13px;
  padding: 4px 8px;
}
.btn-remover-item {
  background: #ffe5e5;
  color: #dc3545;
  border: none;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  line-height: 1;
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
  transition: all 0.2s;
}
.btn-remover-item:hover {
  background: #dc3545;
  color: #fff;
}
.btn-adicionar-item {
  display: block;
  width: 100%;
  margin-top: 6px;
  background: #f8f4f3;
  color: #4C1C17;
  border: 1px dashed #c8b8b4;
  border-radius: 6px;
  padding: 6px;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s;
}
.btn-adicionar-item:hover {
  background: #4C1C17;
  color: #fff;
  border-color: #4C1C17;
}
.composicao-total {
  margin-top: 6px;
  background: #f8f4f3;
  border-radius: 6px;
  border-bottom: none;
  font-size: 15px;
  font-weight: 700;
  color: #4C1C17;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.composicao-valor-total {
  font-weight: 800;
  color: #4C1C17;
}
.composicao-repasse-final {
  margin-top: 8px;
  background: #e8f5e9;
  border-radius: 6px;
  border-bottom: none;
  font-size: 16px;
  font-weight: 800;
  color: #28a745;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.composicao-valor-repasse {
  font-weight: 800;
  color: #28a745;
  font-size: 20px;
}

/* ===== Select Tipo Saída ===== */
.select-tipo-saida {
  display: inline-block;
  width: auto;
  min-width: 160px;
  border-radius: 6px;
  border: 1px solid #ddd;
  padding: 9px 12px;
  font-size: 14px;
  transition: all 0.2s;
}
.select-tipo-saida:focus {
  border-color: #4C1C17;
  box-shadow: 0 0 0 3px rgba(76,28,23,0.1);
}

/* ===== Botões ===== */
.btn-gerar-pix {
  background: linear-gradient(135deg, #4C1C17, #6b2a22);
  color: #fff;
  border: none;
  padding: 14px 48px;
  font-size: 15px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 8px;
  transition: all 0.2s;
  box-shadow: 0 4px 16px rgba(76,28,23,0.3);
}
.btn-gerar-pix:hover {
  background: linear-gradient(135deg, #6b2a22, #8a3530);
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(76,28,23,0.4);
}

.btn-copiar {
  background: linear-gradient(135deg, #6c757d, #495057);
  color: #fff;
  border: none;
  padding: 10px 32px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  transition: all 0.2s;
}
.btn-copiar:hover {
  background: linear-gradient(135deg, #868e96, #6c757d);
  color: #fff;
  transform: translateY(-1px);
}

.btn-confirmar-pix {
  background: linear-gradient(135deg, #28a745, #218838);
  color: #fff;
  border: none;
  padding: 12px 44px;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 8px;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}
.btn-confirmar-pix:hover {
  background: linear-gradient(135deg, #34ce57, #28a745);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(40, 167, 69, 0.4);
}

.btn-enviar-relatorio {
  background: linear-gradient(135deg, #d4a017, #b8860b);
  color: #fff;
  border: none;
  padding: 6px 16px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  transition: all 0.2s;
}
.btn-enviar-relatorio:hover {
  background: linear-gradient(135deg, #e6b422, #d4a017);
  color: #fff;
  transform: translateY(-1px);
}

/* ===== Tabela ===== */
.table-repasses thead {
  background: #4C1C17;
  color: #fff;
}
.table-repasses th {
  padding: 12px 16px;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: none;
}
.table-repasses td {
  padding: 12px 16px;
  vertical-align: middle;
  font-size: 14px;
}
.table-repasses tbody tr:hover {
  background: #f8f4f3;
}
.table-repasses tbody tr:last-child td {
  border-bottom: none;
}
.valor-cell {
  font-weight: 700;
  color: #4C1C17;
}

/* ===== FAB Buscar Locações ===== */
.fab-buscar-locacoes {
  position: fixed;
  bottom: 28px;
  right: 28px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #4C1C17, #6b2a22);
  color: #fff;
  padding: 14px 22px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.5px;
  box-shadow: 0 6px 20px rgba(76,28,23,0.4);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.fab-icone {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
}
.fab-icone svg {
  width: 24px;
  height: 24px;
}
.fab-texto {
  max-width: 0;
  overflow: hidden;
  white-space: nowrap;
  opacity: 0;
  transition: all 0.3s ease;
}
.fab-buscar-locacoes:hover {
  background: linear-gradient(135deg, #6b2a22, #8a3530);
  transform: translateY(-3px);
  box-shadow: 0 10px 28px rgba(76,28,23,0.5);
}
.fab-buscar-locacoes:hover .fab-texto {
  max-width: 140px;
  opacity: 1;
}
.fab-buscar-locacoes::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50px;
  border: 2px solid #4C1C17;
  animation: fabPulse 2s infinite;
}
@keyframes fabPulse {
  0%   { transform: scale(1);   opacity: 0.8; }
  70%  { transform: scale(1.12); opacity: 0; }
  100% { transform: scale(1.12); opacity: 0; }
}

/* ===== Modal de prévia do relatório ===== */
.modal-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #4C1C17;
  color: #fff;
  padding: 12px 20px;
}
.modal-preview-header h5 {
  color: #fff;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 14px;
  margin: 0;
}
.modal-preview-close {
  background: transparent;
  color: #fff;
  border: none;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s;
}
.modal-preview-close:hover {
  color: #ffc9c0;
}
.modal-preview-corpo {
  padding: 16px;
  background: #f4f1ef;
}
.modal-preview-iframe {
  width: 100%;
  height: 480px;
  border: 1px solid #e5dfdb;
  border-radius: 6px;
  background: #fff;
}
.modal-preview-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid #e8e4e3;
}
.btn-cancelar-preview {
  background: #6c757d;
  color: #fff;
  border: none;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  transition: all 0.2s;
}
.btn-cancelar-preview:hover {
  background: #5a6268;
  color: #fff;
}
.btn-enviar-preview {
  background: linear-gradient(135deg, #28a745, #218838);
  color: #fff;
  border: none;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 6px;
  transition: all 0.2s;
}
.btn-enviar-preview:hover:not(:disabled) {
  background: linear-gradient(135deg, #34ce57, #28a745);
  color: #fff;
  transform: translateY(-1px);
}
.btn-enviar-preview:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>

<style>
/* Estilo da raiz do modal criada pela lib vue-js-modal (fora do escopo do
   componente). Sem isso o conteúdo da prévia ficaria muito colado nas bordas. */
.vue-modal-preview-relatorio {
  border-radius: 10px;
  overflow: hidden;
}
</style>